import * as React from 'react';
import { addMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { Button, CircularProgress, Typography } from '@mui/material';
import { ServiceCategory } from '@prisma/client';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Month from './Days/Month';
import CalendarHeader from './CalendarHeader';
import CalendarNav from './CalendarNav';
import TimeList from './Times/TimeList';
import './calendar.css';
import CustomDate from './Times/CustomDate';
import useEmployeeAvailableDays from '../../hooks/appointments/useEmployeeAvailableDays';
import TextInput from '../form/TextInput';
import { TakenSessionSlot } from '../../types/appointments';
import { OfferedSlotChips } from './SelectedSlotChips';
import { cx } from '../../helpers/global';

type Props = {
  label?: string;
  name?: string;
  employeeId: string;
  appointmentLengthInMinutes: string;
  startDateTime: string | undefined;
  rescheduleAppointmentId?: string;
  takenSlots?: TakenSessionSlot[];
  onChange: (value: Date) => void;
  onDelete?: () => void;
  disabled: boolean;
  timezone: string;
  serviceCategory?: ServiceCategory;
  error?: string;
  customerView?: boolean;
  /** Open the picker straight away, for callers whose only job is picking */
  openOnMount?: boolean;
  hideTrigger?: boolean;
  /** Replaces Done, for callers that commit rather than just close */
  onDone?: () => void;
  onDismiss?: () => void;
  doneLabel?: string;
  selectedDateTimes?: string[];
  maxSelections?: number;
  /** Rendered above the grid, for anything the caller changed on the way in */
  notice?: React.ReactNode;
};

export default function Calendar({
  label,
  name,
  employeeId,
  appointmentLengthInMinutes,
  startDateTime,
  rescheduleAppointmentId,
  takenSlots,
  onChange,
  onDelete,
  disabled,
  timezone,
  serviceCategory,
  error,
  customerView,
  openOnMount,
  hideTrigger,
  onDone,
  onDismiss,
  doneLabel,
  selectedDateTimes,
  maxSelections,
  notice,
}: Props) {
  const isMulti = (maxSelections ?? 1) > 1;
  const selectedStarts = selectedDateTimes ?? [];
  const [usersTime, setUsersTime] = React.useState(
    formatInTimeZone(new Date(), timezone, 'h:mm a'),
  );
  const [selectedDay, setSelectedDay] = React.useState(new Date());
  const [dayIsSelected, setDayIsSelected] = React.useState(false);
  const [formattedDateString, setFormattedDateString] = React.useState('');
  // Same guards as handleOpen: without an artist or a length there is
  // nothing to show, so it stays shut and the field says why
  const [open, setOpen] = React.useState(
    !!openOnMount && !disabled && !!employeeId && !!appointmentLengthInMinutes,
  );
  const [dateError, setDateError] = React.useState('');
  const handleOpen = () => {
    if (disabled) return;

    // Availability depends on both, so name only what's actually missing
    const missing = [
      !employeeId && 'an artist',
      !appointmentLengthInMinutes && 'an appointment length',
    ].filter(Boolean);

    if (missing.length) {
      setDateError(`Select ${missing.join(' and ')} to see available times`);
      return;
    }

    setDateError('');
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    onDismiss?.();
  };

  // One size everywhere: the times column is always reserved, so the panel
  // is 540 wherever it fits and edge-to-edge on a phone
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 32px)',
    maxWidth: 540,
  };
  React.useEffect(() => {
    if (startDateTime) {
      const newString = formatInTimeZone(
        new Date(startDateTime),
        timezone,
        'MMMM do, yyyy, h:mma',
      );
      let endString = '';
      if (appointmentLengthInMinutes) {
        const endDateTime = addMinutes(
          new Date(startDateTime),
          parseInt(appointmentLengthInMinutes, 10),
        );
        endString = formatInTimeZone(endDateTime, timezone, 'h:mma z');
      }

      setFormattedDateString(`${newString} - ${endString}`);
    } else setFormattedDateString('');
  }, [appointmentLengthInMinutes, timezone, startDateTime]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const date = new Date();
      const userTimeString = formatInTimeZone(date, timezone, 'h:mm a');
      setUsersTime(userTimeString);
    }, 30000);

    return () => clearInterval(interval);
  }, [timezone]);

  const {
    query: {
      data: availableDays,
      isFetching,
      isError,
      isLoading: isLoadingInitial,
    },
    state: { initialCurrentMonth, selectedMonth, setSelectedMonth },
    noAvailableDays,
  } = useEmployeeAvailableDays({
    duration: appointmentLengthInMinutes,
    employeeId,
    rescheduleAppointmentId,
    serviceCategory,
    takenSlots,
    // Offering keeps the customer-view UI rules but asks for the artist's own
    // grid, so only the client's booking-window filters are skipped
    customerView,
  });

  const loading = isFetching;
  const uiMonth = selectedMonth || initialCurrentMonth;

  // Offering picks times off a day, so a free-entry datetime that skips the
  // grid has nowhere to land
  const showCustomDate = !customerView && !isMulti;

  const multiSelectLabel = selectedStarts.length
    ? `${selectedStarts.length} of ${maxSelections} times selected`
    : '';

  const picker = (
    <div className="schedule-box">
      {notice && <div className="picker-notice">{notice}</div>}
      <div className="picker-body has-times">
        <div className={cx('calendar-box', dayIsSelected && 'hidden')}>
          <CalendarNav
            selectedMonth={uiMonth}
            setSelectedMonth={setSelectedMonth}
            hideButtons={!!noAvailableDays || isLoadingInitial}
          />
          <CalendarHeader />

          {/* Every state renders inside this fixed grid area, so
                  loading, empty and 5- vs 6-week months are all the same
                  height */}
          <div className="calendar-body">
            {loading && (
              <Box className="calendar-body-centered">
                <CircularProgress color="primary" />
              </Box>
            )}

            {!loading && noAvailableDays && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2" paddingY={1}>
                  No available times found for this appointment
                </Typography>
                {showCustomDate && <CustomDate onChange={onChange} />}
              </Box>
            )}

            {!loading && !noAvailableDays && (
              <Month
                dayItems={availableDays}
                selectedMonth={uiMonth}
                selectedDay={dayIsSelected || isMulti ? selectedDay : undefined}
                setSelectedDay={setSelectedDay}
                setDayIsSelected={setDayIsSelected}
                disableUnavailable={customerView}
              />
            )}

            {isError && !availableDays?.length && !noAvailableDays ? (
              <>
                <Typography variant="body2" paddingY={1}>
                  Error loading calendar for this artist. Please refresh the
                  page and try again
                </Typography>
                {showCustomDate && <CustomDate onChange={onChange} />}
              </>
            ) : null}
          </div>

          {/* Outside the branches: the zone label shouldn't come and
                  go with the grid */}
          <div className="timezone-box-desktop picker-timezone">
            <Typography variant="h3" className="timezone-header">
              Time zone
            </Typography>
            <Typography variant="body2">
              {timezone} {usersTime}
            </Typography>
          </div>
        </div>
        {dayIsSelected ? (
          <TimeList
            selectedDay={selectedDay}
            duration={appointmentLengthInMinutes}
            employeeId={employeeId}
            timezone={timezone}
            onChange={onChange}
            closeModal={handleClose}
            setDayIsSelected={setDayIsSelected}
            serviceCategory={serviceCategory}
            takenSlots={takenSlots}
            customerView={customerView}
            selectedDateTimes={selectedDateTimes}
            maxSelections={maxSelections}
          />
        ) : null}
        {!dayIsSelected && (
          <div className="time-box placeholder">
            <div className="time-box-header">
              <Typography variant="body1">Times</Typography>
              <Typography variant="body2" className="time-box-meta">
                Choose a day
              </Typography>
            </div>
          </div>
        )}
      </div>
      {isMulti && (
        // Fixed-height bar: picking a time no longer closes the modal,
        // so the way out and the picks it applies to stay put
        <div className="selection-bar">
          <div className="selection-bar-chips">
            {selectedStarts.length ? (
              <OfferedSlotChips
                starts={selectedStarts}
                appointmentLengthInMinutes={appointmentLengthInMinutes}
                timezone={timezone}
                onDelete={start => onChange(new Date(start))}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Times you pick appear here
              </Typography>
            )}
          </div>
          <Button
            variant="contained"
            className="primary"
            onClick={onDone ?? handleClose}
            disabled={!!doneLabel && !selectedStarts.length}
            sx={{
              height: 40,
              borderRadius: '8px',
              fontSize: 'body2.fontSize',
              px: 4,
              whiteSpace: 'nowrap',
            }}>
            {doneLabel || 'Done'}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {!hideTrigger && (
        <>
          <button
            onClick={handleOpen}
            style={{
              display: 'block',
              background: 'transparent',
              border: 'none',
              // A button takes its font from the UA sheet, so the label inside
              // it would not inherit the page's.
              fontFamily: 'inherit',
              width: '100%',
              paddingLeft: 0,
              paddingRight: 0,
              marginTop: -1,
              marginBottom: -1,
            }}
            type="button">
            <TextInput
              type="text"
              name={name || 'dateTime'}
              label={label || 'Date & Time *'}
              value={isMulti ? multiSelectLabel : formattedDateString}
              disabled={disabled}
              placeholder={
                isMulti
                  ? `Select up to ${maxSelections} times`
                  : 'Select a date and time'
              }
              isError={!!dateError || !!error}
              endAdornment={
                onDelete
                  ? {
                      icon: CancelOutlinedIcon,
                      onClick: onDelete,
                    }
                  : undefined
              }
              readOnly
            />
          </button>
          {dateError && (
            <Box className="error-message" my={0.5}>
              <Typography variant="body1" color="primary">
                {dateError}
              </Typography>
            </Box>
          )}
        </>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={style}>{picker}</Box>
      </Modal>
    </>
  );
}
